// ============================================
// 数据源管理面板
// 添加/删除/连接/断开数据源
// ============================================

import { useState } from 'react';
import {
  List, Button, Select, Input, Space, Typography, Tag, Popconfirm, Modal, Form, InputNumber, message, Alert,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, LinkOutlined, DisconnectOutlined,
  ApiOutlined, CloudOutlined, DatabaseOutlined, WifiOutlined, RobotOutlined,
} from '@ant-design/icons';
import { useDataBindingStore } from '../../stores/dataBindingStore';
import { generateId } from '@dtwin/shared';
import { aiDraftDataSource, testDataSourceConnection, type DataSourceDraft } from '../../api/dataSources';
import type { DatasourceConfig, MqttConfig, RestConfig, WebSocketConfig, StaticConfig } from '../../types/designer';

const { Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
  connected: '#52c41a',
  connecting: '#faad14',
  disconnected: '#888',
  error: '#ff4d4f',
};

const TYPE_ICONS: Record<string, React.ReactNode> = {
  mqtt: <CloudOutlined />,
  rest: <ApiOutlined />,
  websocket: <WifiOutlined />,
  static: <DatabaseOutlined />,
};

export default function DataSourcePanel() {
  const {
    datasources,
    runtimeStates,
    addDatasource,
    removeDatasource,
    connectDatasource,
    disconnectDatasource,
    datasourceManager,
    selectedDatasourceId,
    setSelectedDatasource,
  } = useDataBindingStore();

  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  // B2：AI 数据源草稿
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiWarnings, setAiWarnings] = useState<string[]>([]);
  const [aiDraft, setAiDraft] = useState<DataSourceDraft | null>(null);
  const [aiTest, setAiTest] = useState<{ ok: boolean; message: string } | null>(null);

  const runAiDraft = async () => {
    if (!aiPrompt.trim()) {
      message.warning('请描述数据源需求，如：订阅 rtu/+/up/status 的 MQTT 数据');
      return;
    }
    setAiLoading(true);
    setAiDraft(null);
    setAiTest(null);
    try {
      const r = await aiDraftDataSource(aiPrompt.trim());
      setAiDraft(r.draft);
      setAiWarnings(r.warnings ?? []);
      if (r.draft.type === 'rest') {
        const t = await testDataSourceConnection('rest', r.draft.config);
        setAiTest({ ok: t.ok, message: t.message });
      }
    } catch (e) {
      message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'AI 草稿生成失败');
    } finally {
      setAiLoading(false);
    }
  };

  const fillFromAiDraft = () => {
    if (!aiDraft) return;
    const c = aiDraft.config as Record<string, any>;
    form.resetFields();
    form.setFieldsValue({
      name: aiDraft.name,
      type: aiDraft.type,
      ...(aiDraft.type === 'rest' ? { url: c.url, method: c.method, interval: c.interval ?? 5000 } : {}),
      ...(aiDraft.type === 'websocket' ? { wsUrl: c.url, wsProtocol: c.protocol } : {}),
      ...(aiDraft.type === 'mqtt' ? { broker: c.broker, port: c.port, topic: c.topic, qos: c.qos, username: c.username, password: c.password } : {}),
      ...(aiDraft.type === 'static' ? { staticData: JSON.stringify(c.data ?? {}, null, 2) } : {}),
    });
    setAiOpen(false);
    setModalOpen(true);
    message.success('已填入草稿，请核对后「确定」添加');
  };

  // 监听数据源状态变化
  const handleConnect = async (id: string) => {
    try {
      await connectDatasource(id);
      message.success('连接成功');
    } catch (e: any) {
      // 2026-09-14 修复（FE-56）：失败显示具体原因（原 store 吞异常，失败也提示"连接成功"）
      message.error(`连接失败：${e?.message || '请检查地址/服务是否可用'}`);
    }
  };

  const handleDisconnect = (id: string) => {
    disconnectDatasource(id);
    message.info('已断开');
  };

  const handleAdd = () => {
    form.validateFields().then((values) => {
      const type = values.type;
      let config: DatasourceConfig['config'];

      switch (type) {
        case 'mqtt':
          config = {
            type: 'mqtt',
            broker: values.broker || 'ws://localhost',
            port: values.port || 8083,
            topic: values.topic || 'test/#',
            qos: values.qos ?? 0,
            username: values.username || undefined,
            password: values.password || undefined,
          } as MqttConfig;
          break;
        case 'rest':
          config = {
            type: 'rest',
            url: values.url || 'https://api.example.com/data',
            method: values.method || 'GET',
            headers: values.headers ? JSON.parse(values.headers) : undefined,
            interval: values.interval || 5000,
          } as RestConfig;
          break;
        case 'websocket':
          config = {
            type: 'websocket',
            url: values.wsUrl || 'ws://localhost:8080',
            protocol: values.wsProtocol || undefined,
          } as WebSocketConfig;
          break;
        case 'static':
          config = {
            type: 'static',
            data: values.staticData ? JSON.parse(values.staticData) : { value: 0 },
          } as StaticConfig;
          break;
        default:
          return;
      }

      const newDs: DatasourceConfig = {
        id: generateId(),
        name: values.name || `${type} 数据源`,
        type,
        config,
      };

      addDatasource(newDs);
      setModalOpen(false);
      form.resetFields();
      message.success('数据源已添加');
    });
  };

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Text strong>数据源</Text>
        <Space size={6}>
          <Button size="small" icon={<RobotOutlined />} onClick={() => { setAiPrompt(''); setAiDraft(null); setAiTest(null); setAiWarnings([]); setAiOpen(true); }} data-testid="ds-ai-btn">
            AI 生成
          </Button>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
            添加
          </Button>
        </Space>
      </div>

      <List
        dataSource={datasources}
        locale={{ emptyText: '暂无数据源' }}
        renderItem={(ds) => (
          <List.Item
            style={{
              padding: '8px 0',
              cursor: 'pointer',
              background: selectedDatasourceId === ds.id ? 'rgba(24,144,255,0.1)' : undefined,
              borderRadius: 4,
            }}
            onClick={() => setSelectedDatasource(ds.id)}
          >
            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                  {TYPE_ICONS[ds.type]}
                  <Text style={{ fontSize: 13 }}>{ds.name}</Text>
                </Space>
                <Space size={4}>
                  <Tag color={STATUS_COLORS[runtimeStates[ds.id]?.status || 'disconnected']} style={{ fontSize: 10 }}>
                    {runtimeStates[ds.id]?.status || 'disconnected'}
                  </Tag>
                  <Button
                    type="text"
                    size="small"
                    icon={<LinkOutlined />}
                    onClick={(e) => { e.stopPropagation(); handleConnect(ds.id); }}
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<DisconnectOutlined />}
                    onClick={(e) => { e.stopPropagation(); handleDisconnect(ds.id); }}
                  />
                  <Popconfirm title="删除数据源？" onConfirm={(e) => { e?.stopPropagation(); removeDatasource(ds.id); }}>
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
                  </Popconfirm>
                </Space>
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                {ds.type === 'mqtt' && `MQTT: ${(ds.config as MqttConfig).topic}`}
                {ds.type === 'rest' && `REST: ${(ds.config as RestConfig).method} ${(ds.config as RestConfig).url}`}
                {ds.type === 'websocket' && `WS: ${(ds.config as WebSocketConfig).url}`}
                {ds.type === 'static' && '静态数据'}
              </div>
            </div>
          </List.Item>
        )}
      />

      <Modal
        title="添加数据源"
        open={modalOpen}
        onOk={handleAdd}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        width={480}
      >
        <Form form={form} layout="vertical" initialValues={{ type: 'rest', method: 'GET', port: 8083, interval: 5000, qos: 0 }}>
          <Form.Item name="name" label="名称">
            <Input placeholder="数据源名称" />
          </Form.Item>
          <Form.Item name="type" label="类型">
            <Select options={[
              { value: 'rest', label: 'REST API' },
              { value: 'websocket', label: 'WebSocket' },
              { value: 'mqtt', label: 'MQTT' },
              { value: 'static', label: '静态数据' },
            ]} />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.type !== cur.type}>
            {({ getFieldValue }) => {
              const type = getFieldValue('type');
              if (type === 'rest') return (
                <>
                  <Form.Item name="url" label="URL" rules={[{ required: true }]}>
                    <Input placeholder="https://api.example.com/data" />
                  </Form.Item>
                  <Form.Item name="method" label="方法">
                    <Select options={[
                      { value: 'GET', label: 'GET' },
                      { value: 'POST', label: 'POST' },
                    ]} />
                  </Form.Item>
                  <Form.Item name="interval" label="轮询间隔 (ms)">
                    <InputNumber min={1000} step={1000} style={{ width: '100%' }} />
                  </Form.Item>
                </>
              );
              if (type === 'websocket') return (
                <>
                  <Form.Item name="wsUrl" label="URL" rules={[{ required: true }]}>
                    <Input placeholder="ws://localhost:8080" />
                  </Form.Item>
                  <Form.Item name="wsProtocol" label="协议">
                    <Input placeholder="可选" />
                  </Form.Item>
                </>
              );
              if (type === 'mqtt') return (
                <>
                  <Form.Item name="broker" label="Broker" rules={[{ required: true }]}>
                    <Input placeholder="ws://localhost 或 mqtts://broker.example.com" />
                  </Form.Item>
                  <Form.Item name="port" label="端口">
                    <InputNumber min={1} max={65535} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name="topic" label="Topic" rules={[{ required: true }]}>
                    <Input placeholder="sensor/temperature" />
                  </Form.Item>
                  <Form.Item name="qos" label="QoS">
                    <Select options={[
                      { value: 0, label: '0 - 最多一次' },
                      { value: 1, label: '1 - 至少一次' },
                      { value: 2, label: '2 - 恰好一次' },
                    ]} />
                  </Form.Item>
                  <Form.Item name="username" label="用户名">
                    <Input placeholder="可选" />
                  </Form.Item>
                  <Form.Item name="password" label="密码">
                    <Input.Password placeholder="可选" />
                  </Form.Item>
                </>
              );
              if (type === 'static') return (
                <Form.Item name="staticData" label="数据 (JSON)">
                  <Input.TextArea rows={4} placeholder='{"value": 42}' />
                </Form.Item>
              );
              return null;
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* B2：AI 数据源草稿 */}
      <Modal
        title={<Space><RobotOutlined /> AI 生成数据源草稿</Space>}
        open={aiOpen}
        onCancel={() => setAiOpen(false)}
        footer={
          <Space>
            <Button onClick={() => setAiOpen(false)}>取消</Button>
            {aiDraft?.type === 'rest' ? (
              <Button
                onClick={async () => {
                  const t = await testDataSourceConnection('rest', aiDraft.config).catch((e) => ({ ok: false, message: String(e?.message ?? '测试失败') }));
                  setAiTest({ ok: t.ok, message: t.message });
                }}
              >
                测试连通性
              </Button>
            ) : null}
            <Button type="primary" disabled={!aiDraft} onClick={fillFromAiDraft} data-testid="ds-ai-fill">
              填入表单
            </Button>
          </Space>
        }
        width={520}
        destroyOnHidden
      >
        <Space orientation="vertical" style={{ width: '100%' }} size={10}>
          <Input.TextArea
            rows={3}
            placeholder="用一句话描述，如：订阅 rtu/+/up/status 的设备状态 MQTT；或：拉取 https://api.x.com/weather 每 5 秒"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            data-testid="ds-ai-prompt"
          />
          <Button type="primary" loading={aiLoading} onClick={() => void runAiDraft()} data-testid="ds-ai-generate">
            生成草稿
          </Button>
          {aiWarnings.length > 0 ? <Alert type="warning" showIcon message={`提示：${aiWarnings.join('；')}`} /> : null}
          {aiTest ? (
            <Alert type={aiTest.ok ? 'success' : 'warning'} showIcon message={`连通性：${aiTest.message}`} />
          ) : null}
          {aiDraft ? (
            <div style={{ background: '#0f1420', border: '1px solid #223', borderRadius: 6, padding: 10 }}>
              <Space size={6} wrap style={{ marginBottom: 6 }}>
                <Tag color="blue">{aiDraft.type}</Tag>
                <Text strong style={{ fontSize: 13 }}>{aiDraft.name}</Text>
              </Space>
              <pre style={{ margin: 0, fontSize: 11, color: '#9cf', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(aiDraft.config, null, 2)}</pre>
            </div>
          ) : null}
        </Space>
      </Modal>
    </div>
  );
}
